import './style.modules.scss'

import {
  Content,
  Link,
  LinkTarget,
  LinkVariant,
  Spacer
} from '@ampcore/adel-react'
import { SpacerSpacing } from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import React from 'react'
import xss from 'xss'

import { FacetImage } from '../FacetImage/FacetImage'
import { Image } from '../Image/Image'
import { HeroTopicFrontBackgroundColor } from './HeroTopicFrontColor.type'
import {
  HeroTopicFrontTextAlignment,
  heroTopicFrontTextAlignmentClasses
} from './HeroTopicFrontTextAlignment.type'
import { HeroTopicFrontVariant } from './HeroTopicFrontVariant.type'

export type HeroTopicFrontWithImageProps = {
  variant: HeroTopicFrontVariant.IMAGE
  heading?: string
  body?: string
  image?: string
  imageMobile?: string
  color?: HeroTopicFrontBackgroundColor
  textAlignment?: HeroTopicFrontTextAlignment
  buttonAlignment?: string
  buttonHref?: string
  buttonTarget?: LinkTarget
  buttonText?: string
  analytics?: string
  componentSpacing?: SpacerSpacing
  testId?: string
}

export const HeroTopicFrontWithImage = (
  props: HeroTopicFrontWithImageProps
) => {
  const {
    heading,
    body,
    image,
    imageMobile,
    componentSpacing,
    textAlignment,
    color = BackgroundColor.MIDNIGHT_BLUE,
    buttonAlignment,
    buttonHref,
    buttonTarget,
    buttonText,
    analytics,
    testId
  } = props

  const largeFacet = `${
    window.AMPCOMPATH
  }images/facets/heroes/topic-front/desktop/${
    color || BackgroundColor.MIDNIGHT_BLUE
  }.svg`
  const mobileFacet = `${
    window.AMPCOMPATH
  }images/facets/heroes/topic-front/mobile/${
    color || BackgroundColor.MIDNIGHT_BLUE
  }.svg`

  const textAlignmentClass = textAlignment
    ? heroTopicFrontTextAlignmentClasses[textAlignment]
    : ''

  let bgClassName = ''
  switch (color) {
    case 'light-blue':
      bgClassName = 'u-bgColorBrand'
      break
    case 'purple':
      bgClassName = 'u-bgColorPurple'
      break
    case 'berry':
      bgClassName = 'u-bgColorBerry'
      break
    case 'dark-mint':
      bgClassName = 'u-bgColorDarkMint'
      break
    case 'white':
      bgClassName = 'u-bgColorStone'
      break
    case 'midnight-blue':
    default:
      bgClassName = 'u-bgColorMidnightBlue'
  }

  const isReversedContent = color !== BackgroundColor.WHITE

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className="HeroTopicFront--withImage u-paddingBottom8 u-posRelative"
        data-analytics-container="Hero"
      >
        <FacetImage
          color={color}
          src={largeFacet}
          sm={mobileFacet}
          priority={true}
          className="HeroTopicFront-bgImage u-posAbsolute"
          testId={testId ? `${testId}-facet` : undefined}
        />
        <div
          className={`HeroTopicFront-contentBox u-flex u-sm-flexColReverse u-md-flexColReverse u-lg-flexRow u-sizeConstrained u-posRelative u-paddingHoriz48 u-paddingTop48`}
        >
          <div
            className={`u-lg-size6of12 u-md-sizeFull u-sm-sizeFull Content u-flex u-flexAlignItemsCenter ${textAlignmentClass} ${bgClassName}`}
            data-testid={testId ? `${testId}-box` : null}
          >
            <div className="HeroTopicFront-contentPadding u-flex u-flexCol u-flexJustifyCenter">
              {heading && (
                <h1
                  className={['Type-1', isReversedContent && 'u-textColorWhite']
                    .filter(Boolean)
                    .join(' ')}
                  data-testid={testId ? `${testId}-heading` : null}
                  dangerouslySetInnerHTML={{ __html: xss(heading) }}
                ></h1>
              )}
              {body && (
                <Content
                  large
                  reversed={isReversedContent}
                  className={`u-marginTop8 ${textAlignmentClass}`}
                  dangerouslySetInnerHTML={{ __html: xss(body) }}
                  testId={testId ? `${testId}-body` : undefined}
                />
              )}
              {buttonText && buttonHref && (
                <Link
                  className={[
                    'HeroTopicFront-button',
                    'u-marginTop24',
                    isReversedContent && 'Button--reversed',
                    buttonAlignment
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  variant={LinkVariant.SECONDARY}
                  name={buttonText}
                  url={buttonHref}
                  target={buttonTarget}
                  analyticsName={analytics}
                  testId={testId ? `${testId}-button` : undefined}
                />
              )}
            </div>
          </div>
          <div className="HeroTopicFront-contentImage u-lg-size6of12 u-md-sizeFull u-sm-sizeFull u-posRelative">
            {image && (
              <Image
                src={image}
                sm={imageMobile}
                alt=""
                className="u-sizeFull"
                testId={testId ? `${testId}-image` : undefined}
              />
            )}
          </div>
        </div>
      </section>
    </Spacer>
  )
}
